import { camelCaseProperty } from "css-in-js-utils";
import { parseFloatOrNull, parseInteger } from "replo-utils/lib/math";

/**
 * NOTE (Kevin, 2025-12-10): Parse margin value that can be "auto" or a number.
 * Returns "auto" if the value is "auto", otherwise parses as a float.
 */
function parseMarginValue(
  value: string | null | undefined,
): number | "auto" | null {
  if (value === "auto") {
    return "auto";
  }
  return parseFloatOrNull(value);
}

type StyleLibraryData = {
  styleLibrary?: {
    colors: {
      brand?: Record<string, { color: string; name?: string }>;
      neutrals?: Record<string, string>;
    };
  } | null;
};

/**
 * // NOTE (Nick, 10/29/2025): StyleOverrideElementStyleKey never changes type, but the Value does === 2 keys for value
 */
type StyleOverrideElementStyleKey = string;
type ElementStyleValueFromScaffold = string | null | undefined;
type ElementStylePropFromScaffold = Record<
  StyleOverrideElementStyleKey,
  ElementStyleValueFromScaffold
>;

type StyleOverrideFromScaffold = {
  type: "default" | "styleLibrary";
  styleDefinition: ElementStylePropFromScaffold;
};

type ElementStyleMappingFromScaffold = Record<
  string,
  StyleOverrideFromScaffold
>;

/**
 * // NOTE (Nick, 10/29/2025): The value could be undefined based on helper methods in the final parsing into StyleOverrides
 */
type StyleOverrideElementValueForEditor =
  | string
  | number
  | "auto"
  | null
  | undefined;
type StyleOverrideElementPropForEditor = Record<
  StyleOverrideElementStyleKey,
  StyleOverrideElementValueForEditor
>;

const logicalMapLTR: Record<string, string[]> = {
  /* --- Margin --- */
  "margin-inline": ["marginLeft", "marginRight"],
  "margin-inline-start": ["marginLeft"],
  "margin-inline-end": ["marginRight"],
  "margin-block": ["marginTop", "marginBottom"],
  "margin-block-start": ["marginTop"],
  "margin-block-end": ["marginBottom"],

  /* --- Padding --- */
  "padding-inline": ["paddingLeft", "paddingRight"],
  "padding-inline-start": ["paddingLeft"],
  "padding-inline-end": ["paddingRight"],
  "padding-block": ["paddingTop", "paddingBottom"],
  "padding-block-start": ["paddingTop"],
  "padding-block-end": ["paddingBottom"],

  /* --- Corners --- */
  "border-start-start-radius": ["cornerTopLeftRadius"],
  "border-start-end-radius": ["cornerTopRightRadius"],
  "border-end-start-radius": ["cornerBottomLeftRadius"],
  "border-end-end-radius": ["cornerBottomRightRadius"],

  /* --- Inset / Position --- */
  "inset-inline-start": ["left"],
  "inset-inline-end": ["right"],
  "inset-block-start": ["top"],
  "inset-block-end": ["bottom"],

  /* --- Border widths --- */
  "border-inline-start-width": ["borderLeftWidth"],
  "border-inline-end-width": ["borderRightWidth"],
  "border-block-start-width": ["borderTopWidth"],
  "border-block-end-width": ["borderBottomWidth"],

  /* --- Border styles --- */
  "border-inline-start-style": ["borderLeftStyle"],
  "border-inline-end-style": ["borderRightStyle"],
  "border-block-start-style": ["borderTopStyle"],
  "border-block-end-style": ["borderBottomStyle"],

  /* --- Gap --- */
  "row-gap": ["gap"],
  "column-gap": ["gap"],
};

/**
 * Gets a style library color value from a variable name
 * @param styleLibraryVariable - The variable name to search for (e.g., "primary", "subtle")
 * @param styleLibraryData - The style library data object
 * @returns The resolved color value or null if not found
 */
function getStyleLibraryColor(
  styleLibraryVariable: string,
  styleLibraryData: StyleLibraryData,
): string | null {
  const colors = styleLibraryData.styleLibrary!.colors;

  // Check brand colors first
  const brandColor = colors.brand?.[styleLibraryVariable]?.color;
  if (brandColor) {
    return brandColor;
  }

  // Check neutral colors
  const neutralColor = colors.neutrals?.[styleLibraryVariable];
  if (neutralColor) {
    return neutralColor;
  }

  return null;
}

function formatStyleOverrides(
  rawStyleOverrides: ElementStylePropFromScaffold,
): Record<string, StyleOverrideElementValueForEditor> {
  const formattedStyleOverrides = {
    // Text styles - convert to numbers where appropriate
    fontSize: parseFloatOrNull(rawStyleOverrides.fontSize),
    fontWeight: parseInteger(rawStyleOverrides.fontWeight ?? "0"),
    fontFamily: rawStyleOverrides.fontFamily,
    color: rawStyleOverrides.color,
    lineHeight: parseFloatOrNull(rawStyleOverrides.lineHeight),
    letterSpacing: parseFloatOrNull(rawStyleOverrides.letterSpacing),
    textTransform: rawStyleOverrides.textTransform,
    fontStyle: rawStyleOverrides.fontStyle,
    textAlign: rawStyleOverrides.textAlign,
    textDecoration: rawStyleOverrides.textDecoration,
    textDecorationLine: rawStyleOverrides.textDecorationLine,

    // Size and spacing-related styles
    width: rawStyleOverrides.width,
    height: rawStyleOverrides.height,
    paddingTop: parseFloatOrNull(rawStyleOverrides.paddingTop),
    paddingRight: parseFloatOrNull(rawStyleOverrides.paddingRight),
    paddingBottom: parseFloatOrNull(rawStyleOverrides.paddingBottom),
    paddingLeft: parseFloatOrNull(rawStyleOverrides.paddingLeft),
    marginTop: parseMarginValue(rawStyleOverrides.marginTop),
    marginRight: parseMarginValue(rawStyleOverrides.marginRight),
    marginBottom: parseMarginValue(rawStyleOverrides.marginBottom),
    marginLeft: parseMarginValue(rawStyleOverrides.marginLeft),

    // Container-related styles
    flexDirection: rawStyleOverrides.flexDirection,
    justifyContent: rawStyleOverrides.justifyContent,
    alignItems: rawStyleOverrides.alignItems,
    gap: parseFloatOrNull(rawStyleOverrides.gap),
    display: rawStyleOverrides.display,

    // Background and appearance styles
    backgroundColor: rawStyleOverrides.backgroundColor,
    opacity: parseFloatOrNull(rawStyleOverrides.opacity),

    // // // Corner radius
    cornerTopLeftRadius: parseFloatOrNull(
      rawStyleOverrides.borderTopLeftRadius,
    ),
    cornerTopRightRadius: parseFloatOrNull(
      rawStyleOverrides.borderTopRightRadius,
    ),
    cornerBottomRightRadius: parseFloatOrNull(
      rawStyleOverrides.borderBottomRightRadius,
    ),
    cornerBottomLeftRadius: parseFloatOrNull(
      rawStyleOverrides.borderBottomLeftRadius,
    ),

    // Border styles
    borderTopColor: rawStyleOverrides.borderTopColor,
    borderRightColor: rawStyleOverrides.borderRightColor,
    borderLeftColor: rawStyleOverrides.borderLeftColor,
    borderBottomColor: rawStyleOverrides.borderBottomColor,
    borderTopWidth: parseFloatOrNull(rawStyleOverrides.borderTopWidth),
    borderRightWidth: parseFloatOrNull(rawStyleOverrides.borderRightWidth),
    borderBottomWidth: parseFloatOrNull(rawStyleOverrides.borderBottomWidth),
    borderLeftWidth: parseFloatOrNull(rawStyleOverrides.borderLeftWidth),
    borderTopStyle: rawStyleOverrides.borderTopStyle,
    borderRightStyle: rawStyleOverrides.borderRightStyle,
    borderBottomStyle: rawStyleOverrides.borderBottomStyle,
    borderLeftStyle: rawStyleOverrides.borderLeftStyle,

    // Box shadow
    boxShadow: rawStyleOverrides.boxShadow,
  };
  return formattedStyleOverrides;
}

export function buildStyleOverrides(
  styleOverrides: ElementStyleMappingFromScaffold,
  styleLibraryData?: StyleLibraryData,
): StyleOverrideElementPropForEditor {
  const flattenedStyles: ElementStylePropFromScaffold = {};

  for (const className in styleOverrides) {
    const classStyle = styleOverrides[className];
    if (!classStyle?.styleDefinition) {
      continue;
    }

    // Iterate through each style property in the styleDefinition
    for (const [cssProperty, styleValue] of Object.entries(
      classStyle.styleDefinition,
    ) as Array<[string, string | null]>) {
      let styleOverrideValue = styleValue;
      if (classStyle.type === "styleLibrary" && styleValue) {
        // Extract variable name: "var(--subtle)" -> "subtle"
        const styleLibraryVariable = styleValue.replace(
          /var\(--([^)]+)\)/,
          "$1",
        );
        if (styleLibraryData?.styleLibrary?.colors) {
          styleOverrideValue =
            getStyleLibraryColor(styleLibraryVariable, styleLibraryData) ??
            styleValue;
        }
      }

      // Check if this CSS property needs to be mapped to logical properties
      const mappedProperties = logicalMapLTR[cssProperty];
      if (mappedProperties) {
        for (const mappedProperty of mappedProperties) {
          flattenedStyles[camelCaseProperty(mappedProperty)] =
            styleOverrideValue;
        }
      } else {
        flattenedStyles[camelCaseProperty(cssProperty)] = styleOverrideValue;
      }
    }
  }
  const parsedStyleOverrides = formatStyleOverrides(flattenedStyles);
  return parsedStyleOverrides;
}
